import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, PictureAsPdf, Download } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

interface MapPdfExportProps {
  open: boolean;
  onClose: () => void;
  mapContainerRef?: React.RefObject<HTMLDivElement>; // Make this optional
  visibleLayers: string[];
  measureResults?: Array<{ distance?: number; area?: number; coordinates: number[][] }>;
  pinnedPoints?: Array<{ lat: number; lng: number; label?: string }>;
}

const MapPdfExport: React.FC<MapPdfExportProps> = ({
  open,
  onClose,
  mapContainerRef,
  visibleLayers,
  measureResults = [],
  pinnedPoints = []
}) => {
  const [title, setTitle] = useState('MAADEN Map Export');
  const [isLandscape, setIsLandscape] = useState(true);
  const [selectedLegendItems, setSelectedLegendItems] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string>('');
  const previewRef = useRef<HTMLDivElement>(null);

  // Layer configuration with colors
  const layerConfig = [
    { id: 'couloirs', name: 'Couloirs', color: '#808080' },
    { id: 'mejhars', name: 'Mejhars', color: '#FFA500' },
    { id: 'petite-mine', name: 'Permis petite mine', color: '#FFFF00' },
    { id: 'zones-commerciales', name: 'Zones commerciales', color: '#800080' },
    { id: 'zones-categorie-f', name: 'Zones catégorie F', color: '#008080' },
    { id: 'permis-industriels', name: 'Permis industriels', color: '#8B4513' },
    { id: 'forages', name: 'Forages', color: '#006400' },
    { id: 'centres-traitement', name: 'Centres de traitement', color: '#FF6347' },
    { id: 'routes', name: 'Routes', color: '#FFD700' },
    { id: 'bureaux-maaden', name: 'Bureaux MAADEN', color: '#4169E1' },
    { id: 'mesures', name: 'Mesures', color: '#FF0000' },
    { id: 'points-epingles', name: 'Points épinglés', color: '#00FF00' }
  ];

  // Initialize selected legend items when modal opens
  useEffect(() => {
    if (open) {
      const initialItems = layerConfig
        .filter(layer => visibleLayers.includes(layer.id))
        .map(layer => layer.id);
      
      // Add measurements and pinned points if they exist
      if (measureResults.length > 0) {
        initialItems.push('mesures');
      }
      if (pinnedPoints.length > 0) {
        initialItems.push('points-epingles');
      }
      
      setSelectedLegendItems(initialItems);
      captureMapPreview();
    }
  }, [open, visibleLayers, measureResults, pinnedPoints]);

  const captureMapPreview = async () => {
    // Try to get map container by ref first, then by ID
    const mapContainer = mapContainerRef?.current || document.getElementById('map');
    
    if (!mapContainer) {
      setError('Map container not found');
      return;
    }

    setIsCapturing(true);
    setError('');

    try {
      // Wait a bit for any animations to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get the Mapbox map instance
      const mapInstance = (window as any)._leaflet_map;
      
      if (mapInstance) {
        // Use Mapbox's built-in export functionality
        const canvas = mapInstance.getCanvas();
        const imageData = canvas.toDataURL('image/png', 0.9);
        setPreviewImage(imageData);
      } else {
        // Fallback to html2canvas with better settings
        const canvas = await html2canvas(mapContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: mapContainer.offsetWidth,
          height: mapContainer.offsetHeight,
          foreignObjectRendering: true,
          removeContainer: false,
          // Add specific settings for map capture
          onclone: (clonedDoc) => {
            const clonedMap = clonedDoc.getElementById('map');
            if (clonedMap) {
              // Ensure the cloned map is visible and properly sized
              clonedMap.style.display = 'block';
              clonedMap.style.visibility = 'visible';
              clonedMap.style.opacity = '1';
            }
          }
        });

        const imageData = canvas.toDataURL('image/png', 0.9);
        setPreviewImage(imageData);
      }
    } catch (err) {
      console.error('Error capturing map:', err);
      setError('Failed to capture map preview. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleLegendItemToggle = (layerId: string) => {
    setSelectedLegendItems(prev => 
      prev.includes(layerId) 
        ? prev.filter(id => id !== layerId)
        : [...prev, layerId]
    );
  };

  const generatePDF = async () => {
    if (!previewImage) {
      setError('No map preview available');
      return;
    }

    try {
      setIsCapturing(true);
      setError('');

      // Create PDF document
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Add title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, pageWidth / 2, margin + 10, { align: 'center' });

      // Calculate map dimensions
      const mapWidth = pageWidth - (2 * margin);
      const mapHeight = pageHeight - (2 * margin) - 60; // Space for title and legend

      // Add map image
      pdf.addImage(previewImage, 'PNG', margin, margin + 20, mapWidth, mapHeight);

      // Add legend
      const legendY = margin + 20 + mapHeight + 10;
      const legendX = margin;
      const legendItemHeight = 6;
      const itemsPerColumn = Math.ceil(selectedLegendItems.length / 2);

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Légende:', legendX, legendY);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');

      selectedLegendItems.forEach((layerId, index) => {
        const layer = layerConfig.find(l => l.id === layerId);
        if (!layer) return;

        const column = Math.floor(index / itemsPerColumn);
        const row = index % itemsPerColumn;
        const x = legendX + (column * 80);
        const y = legendY + 8 + (row * legendItemHeight);

        // Draw color box
        pdf.setFillColor(layer.color);
        pdf.rect(x, y - 3, 4, 3, 'F');

        // Add layer name
        pdf.text(layer.name, x + 6, y);
      });

      // Add metadata at bottom
      const metadataY = pageHeight - 10;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.text(`Exporté le: ${new Date().toLocaleString('fr-FR')}`, margin, metadataY);
      pdf.text(`Layers visibles: ${selectedLegendItems.length}`, pageWidth - margin, metadataY, { align: 'right' });

      // Generate QR code with map data
      const qrData = JSON.stringify({
        title,
        layers: selectedLegendItems,
        timestamp: new Date().toISOString(),
        coordinates: measureResults.map(r => r.coordinates),
        pinnedPoints
      });

      const qrCodeDataUrl = await QRCode.toDataURL(qrData, { 
        width: 40, 
        margin: 1,
        color: { dark: '#000000', light: '#FFFFFF' }
      });

      pdf.addImage(qrCodeDataUrl, 'PNG', pageWidth - 25, pageHeight - 25, 20, 20);

      // Save PDF
      pdf.save(`${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(24,29,38,0.95)',
          color: '#fff',
          borderRadius: 2,
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        pb: 2
      }}>
        <Typography variant="h6" sx={{ fontFamily: 'Louguiya, sans-serif', fontWeight: 600 }}>
          Export PDF
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 2, p: 3 }}>
        {/* Left Panel - Controls */}
        <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Title and Orientation */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Titre
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#90caf9' }
                },
                '& .MuiInputBase-input': { color: '#fff' }
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isLandscape}
                  onChange={(e) => setIsLandscape(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#666',
                      '&.Mui-checked': {
                        color: '#90caf9',
                        '& + .MuiSwitch-track': { bgcolor: 'rgba(144,202,249,0.3)' }
                      }
                    }
                  }}
                />
              }
              label="Paysage"
              sx={{ mt: 1, '& .MuiFormControlLabel-label': { color: '#fff' } }}
            />
          </Box>

          {/* Legend Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Légende :
            </Typography>
            <FormGroup>
              {layerConfig.map((layer) => (
                <FormControlLabel
                  key={layer.id}
                  control={
                    <Checkbox
                      checked={selectedLegendItems.includes(layer.id)}
                      onChange={() => handleLegendItemToggle(layer.id)}
                      sx={{
                        color: layer.color,
                        '&.Mui-checked': { color: layer.color }
                      }}
                    />
                  }
                  label={layer.name}
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      color: '#fff', 
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif'
                    }
                  }}
                />
              ))}
            </FormGroup>
          </Box>

          {error && (
            <Alert severity="error" sx={{ bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Right Panel - Preview */}
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Aperçu
          </Typography>
          <Paper
            ref={previewRef}
            sx={{
              flex: 1,
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              position: 'relative'
            }}
          >
            {isCapturing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} sx={{ color: '#90caf9' }} />
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Capture de la carte...
                </Typography>
              </Box>
            ) : previewImage ? (
              <img
                src={previewImage}
                alt="Map Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: '#ccc', textAlign: 'center' }}>
                Aperçu de la carte
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#ccc',
            borderColor: 'rgba(255,255,255,0.2)',
            '&:hover': { borderColor: '#ccc' }
          }}
        >
          ANNULER
        </Button>
        <Button
          onClick={captureMapPreview}
          variant="outlined"
          startIcon={<PictureAsPdf />}
          disabled={isCapturing}
          sx={{
            color: '#90caf9',
            borderColor: '#90caf9',
            '&:hover': { borderColor: '#64b5f6' }
          }}
        >
          Actualiser
        </Button>
        <Button
          onClick={generatePDF}
          variant="contained"
          startIcon={<Download />}
          disabled={isCapturing || !previewImage}
          sx={{
            bgcolor: '#90caf9',
            color: '#000',
            '&:hover': { bgcolor: '#64b5f6' },
            '&:disabled': { bgcolor: 'rgba(144,202,249,0.3)' }
          }}
        >
          ENREGISTRER
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPdfExport;
